import TableContainer from "components/Common/TableContainer"
import React, { useMemo, useRef, useState } from "react"
import { Button, Input } from "reactstrap"
import { checkForMinusNumber, numberIsSame } from "utils/utils"

const ReceiptsList = ({
  loading,
  data,
  onSubmitAddHandler,
  submited,
  drafts,
  selectedFiles,
}) => {
  const [outQuantity, setOutQuantity] = useState([])
  const inputRef = useRef()

  // Calculate total distributed quantity for each provider_in_id from drafts
  const getTotalDistributedQuantity = providerInId => {
    if (!drafts || !drafts.data) return 0

    const draftsForItem = drafts.data.filter(
      draft => draft.data.provider_in_id === providerInId
    )

    return draftsForItem.reduce((total, draft) => {
      return total + parseFloat(draft.data.out_quantity || 0)
    }, 0)
  }

  const handleChangeQuantity = (e, receipt) => {
    if (checkForMinusNumber(e)) {
      e.target.value = 0
      return false
    }

    inputRef.current = receipt.unique_id
    const newItem = { id: receipt.unique_id, quantity: e.target.value }
    setOutQuantity(prev =>
      prev.some(p => p.id === receipt.unique_id)
        ? prev.map(qty =>
            qty.id === newItem.id ? { ...qty, quantity: e.target.value } : qty
          )
        : [...prev, newItem]
    )
  }

  function showTotal(quantity, id) {
    const totalDistributed = getTotalDistributedQuantity(id)
    const value = outQuantity.find(qty => qty.id === id)

    if (value) {
      return quantity - totalDistributed - Math.abs(value.quantity)
    } else {
      return quantity - totalDistributed
    }
  }

  const onAddHandler = (outQuantityValue, receipt) => {
    const quantity = outQuantityValue.quantity
    const unfpa_quantity_receipt = receipt?.unit_uom_quantity_per_unfpa
    let unfpa_quantity
    const ip_quantity = receipt?.unit_umo_quantity_per_ip

    if (!numberIsSame(unfpa_quantity_receipt, ip_quantity)) {
      unfpa_quantity = quantity / unfpa_quantity_receipt
    } else {
      unfpa_quantity = quantity
    }

    const total_value = unfpa_quantity * receipt?.unit_price_per_unfpa

    const result = {
      unfpa_quantity: unfpa_quantity,
      total_value: total_value,
      out_quantity: quantity,
      provider_in_id: outQuantityValue.id,
      batch_number: receipt?.batch_number,
      project_id: receipt?.project_id,
      document: selectedFiles,
    }

    onSubmitAddHandler(result)
    onResetReceiptList()
  }

  const onResetReceiptList = () => {
    setOutQuantity([])
    // Clear the input value safely
    if (inputRef.current) {
      inputRef.current.value = ""
    }
  }

  function checkForAdd(receipt) {
    const outQuantityValue = outQuantity.find(
      qty => qty.id === receipt?.unique_id
    )

    if (outQuantityValue) {
      const totalDistributed = getTotalDistributedQuantity(receipt.unique_id)

      const totalAvailable = receipt?.in_qty_per_ip - totalDistributed
      const inputQuantity = outQuantityValue.quantity

      if (inputQuantity <= 0) {
        return false
      }

      if (inputQuantity <= totalAvailable) {
        return (
          <Button
            className="btn btn-sm btn-info"
            onClick={() => onAddHandler(outQuantityValue, receipt)}
            type="button"
          >
            Add
          </Button>
        )
      } else {
        return <span className="text-danger">Out of Quantity</span>
      }
    } else {
      return
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "UNFPA ITEM ID",
        accessor: "item.unfp_item_name",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps?.value
        },
      },
      {
        Header: "IP- ITEM DESCRIPTION",
        accessor: "item.ip_item_description",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps?.value
        },
      },
      {
        Header: "UNFPA UOM",
        accessor: "item",
        id: "unpfa_item",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return `${cellProps?.value?.uom} ${
            cellProps?.value?.unfpa_uom_quantity > 1 &&
            cellProps?.value?.unfpa_uom_quantity
          }`
        },
      },
      {
        Header: "IP UOM",
        accessor: "item",
        id: "ip_item",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return `${cellProps?.value?.ip_unit_umo} ${
            cellProps?.value?.ip_uom_quantity > 1 &&
            cellProps?.value?.ip_uom_quantity
          }`
        },
      },
      {
        Header: "BATCH NUMBER",
        accessor: "batch_number",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps?.value
        },
      },
      {
        Header: "EXPIRE DATE",
        accessor: "expire_date",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps?.value
        },
      },
      {
        Header: "Original Quantity",
        accessor: "in_qty_per_ip",
        filterable: false,
        disableFilters: true,
        Cell: cellProps => {
          return cellProps?.value
        },
      },
      {
        Header: "Already Distributed",
        filterable: false,
        disableFilters: true,
        Cell: ({ row }) => {
          const totalDistributed = getTotalDistributedQuantity(
            row?.original?.unique_id
          )
          return totalDistributed
        },
      },
      {
        Header: "Out",
        filterable: false,
        disableFilters: true,
        Cell: ({ row }) => {
          return (
            <Input
              key={row?.original?.unique_id}
              type="number"
              name={`quantity-${row?.original.unique_id}`}
              onWheel={e => e.target.blur()}
              ref={inputRef}
              disabled={loading}
              min={0}
              style={{ width: 100 }}
              max={
                row?.original?.in_qty_per_ip -
                getTotalDistributedQuantity(row?.original?.unique_id)
              }
              value={
                outQuantity?.find(qty => qty.id === row?.original?.unique_id)
                  ?.quantity
              }
              onChange={e => handleChangeQuantity(e, row?.original)}
              autoFocus={row?.original?.unique_id === inputRef.current}
            />
          )
        },
      },
      {
        Header: "Balance",
        filterable: false,
        disableFilters: true,
        Cell: ({ row }) => {
          return showTotal(
            row?.original?.in_qty_per_ip,
            row?.original?.unique_id
          )
        },
      },
      {
        Header: "",
        id: "addDistribute",
        filterable: false,
        disableFilters: true,
        Cell: ({ row }) => {
          return checkForAdd(row?.original)
        },
      },
    ],
    [outQuantity, drafts]
  )

  return (
    <React.Fragment>
      <div className="mb-4 h4 card-title">Items</div>
      <TableContainer
        columns={columns}
        data={data}
        isGlobalFilter={false}
        isAddOptions={false}
        customPageSize={6}
      />
    </React.Fragment>
  )
}

export default ReceiptsList
